// src/pages/Welcome.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import PDFViewer from '../components/shared/PDFViewer';
import logonegro from '../assets/images/logonegro.svg';
import fondoPantalla from '../assets/images/fondo_pantalla.svg';
import completeAccountPDF from '../assets/documents/complete-account-guide.pdf';
import '../assets/styles/auth-common.css';
import Footer from '../components/layout/footer';
import styles from '../assets/styles/OnboardingForm.module.css';

// Translations
const translations = {
  EN: {
    welcome: {
      title: "Select your",
      bank: "BELOBABA",
      subtitle: "Account",
      completeAccount: {
        title: "Complete Account",
        description: "Create FIAT wallets in different jurisdictions, crypto wallets in multiple networks and request your Mastercard",
        documentation: "Click here to view Onboarding requirements",
        note: "*Requires full onboarding process"
      },
      expressAccount: {
        title: "Express Account",
        description: "Create crypto wallets in multiple networks with a simplified registration process",
        note: "*You can upgrade to a Complete Account at any time"
      },
      pdfViewer: {
        loading: "Loading documentation...",
        error: "Error loading PDF",
        close: "Close"
      }
    }
  },
  ES: {
    welcome: {
      title: "Seleccione su",
      bank: "Cuenta de",
      subtitle: "BELOBABA",
      completeAccount: {
        title: "Cuenta Completa",
        description: "Cree wallets FIAT en diferentes jurisdicciones, wallets crypto en múltiples redes y solicite su Mastercard",
        documentation: "Click aquí para ver requisitos de Onboarding",
        note: "*Requiere proceso de onboarding completo"
      },
      expressAccount: {
        title: "Cuenta Express",
        description: "Cree wallets crypto en múltiples redes con un proceso de registro simplificado",
        note: "*Puede actualizar a una Cuenta Completa en cualquier momento"
      },
      pdfViewer: {
        loading: "Cargando documentación...",
        error: "Error al cargar el PDF",
        close: "Cerrar"
      }
    }
  }
};

const Welcome: React.FC = () => {
  const navigate = useNavigate();
  const [language, setLanguage] = useState<'EN' | 'ES'>('EN');
  const [showPDF, setShowPDF] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const t = translations[language].welcome;

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage === 'EN' || savedLanguage === 'ES') {
      setLanguage(savedLanguage);
    }
  }, []);

  const handleLanguageChange = (newLanguage: 'EN' | 'ES') => {
    setLanguage(newLanguage);
    localStorage.setItem('language', newLanguage);
  };

  const handleAccountSelection = async (type: 'COMPLETE' | 'EXPRESS', event: React.MouseEvent) => {
    if (isLoading) return;

    const target = event.target as HTMLElement;
    const isPDFLink = target.tagName === 'SPAN' || target.classList.contains('pdf-note');

    if (isPDFLink && type === 'COMPLETE') {
      setShowPDF(true);
      return;
    }

    try {
      setIsLoading(true);
      if (type === 'COMPLETE') {
        window.location.href = '/onboarding';
      } else {
        window.location.href = '/fast-onboarding';
      }
    } catch (error) {
      console.error('Navigation error:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
      });
    };
    window.addEventListener('resize', handleResize);
    handleResize(); // Set initial size
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const rootStyle = {
    '--bg-image': `url(${fondoPantalla})`
  } as React.CSSProperties;

  return (
    <div className="auth-container"  style={windowSize.width >= 640 ? rootStyle : {}}>
      <header className="header-login">
        <img src={logonegro} alt="Belobaba" className="logo" />
        <div className="language-selector">
          <button
            className={language === 'EN' ? 'active' : ''}
            onClick={() => handleLanguageChange('EN')}
          >
            EN
          </button>
          <span style={{ color: '#B3A369' }}>|</span>
          <button
            className={language === 'ES' ? 'active' : ''}
            onClick={() => handleLanguageChange('ES')}
          >
            ES
          </button>
        </div>
      </header>

      <div className="containerlogin" style={{ position: 'relative' }}>
        <div className="containertexto" style={windowSize.width <= 450 ? {marginTop:"0"} : {}}>
          <span className="textologin">
            {t.title}<br />
            {t.bank}<br />
            {t.subtitle}
          </span>
        </div>
        {(windowSize.width <= 450) ? (
          <div 
          className={styles.typeSelectionContainer}
          // style={{
          //   position: 'absolute',
          //   left: '700px',
          //   top: '50%',
          //   transform: 'translateY(-55%)',
          //   width: '400px'
          // }}
          >
          <div 
            className={styles.optionsContainer}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              marginTop: '20px'
            }}
          >
            <div 
              className={`${styles.optionCard} ${isLoading ? styles.disabled : ''}`}
              onClick={(e) => !isLoading && handleAccountSelection('COMPLETE', e)}
              style={{ 
                cursor: isLoading ? 'wait' : 'pointer',
                margin: '0'
              }}
            >
              <h3 className={styles.optionTitle}>{t.completeAccount.title}</h3>
              <p className={styles.optionDescription}>
                {t.completeAccount.description}
                <br /><br />
                <span 
                  className="pdf-note" 
                  style={{ 
                    fontSize: '12px', 
                    cursor: isLoading ? 'wait' : 'pointer', 
                    textDecoration: 'underline',
                    display: 'block',
                    marginBottom: '8px'
                  }}
                >
                  {t.completeAccount.documentation}
                </span>
                <span style={{ fontSize: '12px' }}>
                  {t.completeAccount.note}
                </span>
              </p>
            </div>

            <div 
              className={`${styles.optionCard} ${isLoading ? styles.disabled : ''}`}
              onClick={(e) => !isLoading && handleAccountSelection('EXPRESS', e)}
              style={{ 
                cursor: isLoading ? 'wait' : 'pointer',
                margin: '0'
              }}
            >
              <h3 className={styles.optionTitle}>{t.expressAccount.title}</h3>
              <p className={styles.optionDescription}>
                {t.expressAccount.description}
                <br /><br />
                <span style={{ fontSize: '12px' }}>
                  {t.expressAccount.note}
                </span>
              </p>
            </div>
          </div>
          </div>
        ) :(
          <div 
          className={styles.typeSelectionContainer}
          style={{
            position: 'absolute',
            left: '700px',
            top: '50%',
            transform: 'translateY(-55%)',
            width: '400px'
          }}
          >
          <div 
            className={styles.optionsContainer}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              marginTop: '20px'
            }}
          >
            <div 
              className={`${styles.optionCard} ${isLoading ? styles.disabled : ''}`}
              onClick={(e) => !isLoading && handleAccountSelection('COMPLETE', e)}
              style={{ 
                cursor: isLoading ? 'wait' : 'pointer',
                margin: '0'
              }}
            >
              <h3 className={styles.optionTitle}>{t.completeAccount.title}</h3>
              <p className={styles.optionDescription}>
                {t.completeAccount.description}
                <br /><br />
                <span 
                  className="pdf-note" 
                  style={{ 
                    fontSize: '12px', 
                    cursor: isLoading ? 'wait' : 'pointer', 
                    textDecoration: 'underline',
                    display: 'block',
                    marginBottom: '8px'
                  }}
                >
                  {t.completeAccount.documentation}
                </span>
                <span style={{ fontSize: '12px' }}>
                  {t.completeAccount.note}
                </span>
              </p>
            </div>
      
            <div 
              className={`${styles.optionCard} ${isLoading ? styles.disabled : ''}`}
              onClick={(e) => !isLoading && handleAccountSelection('EXPRESS', e)}
              style={{ 
                cursor: isLoading ? 'wait' : 'pointer',
                margin: '0'
              }}
            >
              <h3 className={styles.optionTitle}>{t.expressAccount.title}</h3>
              <p className={styles.optionDescription}>
                {t.expressAccount.description}
                <br /><br />
                <span style={{ fontSize: '12px' }}>
                  {t.expressAccount.note}
                </span>
              </p>
            </div>
          </div>
          </div>
        )}
       
      </div>

      {showPDF && (
        <PDFViewer
          pdfUrl={completeAccountPDF}
          onClose={() => setShowPDF(false)}
          language={language}
          translations={translations[language].welcome}
        />
      )}

      <Footer />
    </div>
  );
};

export default Welcome;